/**
 * usePreviewSpreadsheet — TanStack mutation wrapper around
 * ``POST /enterprise/connectors/preview-spreadsheet``.
 *
 * Lifecycle:
 *   - The preview is EPHEMERAL — we do NOT invalidate any TanStack cache
 *     on success because nothing persisted server-side. The dialog reads
 *     the response straight off ``mutation.data`` and renders the sheet
 *     picker + sample rows; the user then drives the import via
 *     ``useImportSpreadsheet``.
 *   - Errors are re-thrown so the host dialog can render an inline
 *     ``<InlineErrorBanner>`` — toasts disappear before the user can act
 *     on them, which is the wrong UX for a modal flow.
 *
 * Why a hook (not a one-shot ``previewSpreadsheet(...)`` call): we need
 * ``isPending`` for the dropzone spinner, ``reset()`` so going "Indietro"
 * in the dialog clears the previous preview from the UI, and ``error``
 * so the inline banner stays scoped to this surface (not a global toast).
 */

import { useMutation } from "@tanstack/react-query";

import {
  type PreviewSpreadsheetOptions,
  type SpreadsheetPreviewResponse,
  previewSpreadsheet,
} from "../api/spreadsheetApi";

export function usePreviewSpreadsheet() {
  return useMutation<SpreadsheetPreviewResponse, Error, PreviewSpreadsheetOptions>({
    mutationFn: (options) => previewSpreadsheet(options),
  });
}
